import React from 'react';
import classnames from 'classnames';
import { Badge, Spinner } from 'react-bootstrap';
import { connect } from 'react-redux';
import {
  NavLink,
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';
import { EmailAssociation } from 'v2/models';

import angleIcon from '../../icons/angle.svg';
import detailsIcon from '../../icons/details.svg';
import styles from './contactsDetailsContainer.module.scss';
import { useQuery } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import ContactDetail from './ContactDetail';
import { getContactNameToDisplay } from 'v2/utils/ContactUtils';
import useHistoryBackState from 'v2/hooks/useHistoryBackState';
import { useContact } from 'v2/queries/useContact';
import { useTranslation } from 'react-i18next';
import { PostProcessKeys, TranslationKeys } from 'translations';
import InvalidDRMLinkMessage from 'v2/pages/DrmFeatures/Shared/InvalidDRMLinkMessage/InvalidDRMLinkMessage';

export type ContactsRouterProps = RouteComponentProps<{ id: string }>;

export const ContactsDetailsContainer = ({ match }: ContactsRouterProps) => {
  const { t } = useTranslation();
  const { path, url, params } = match;
  const {
    backLinkTitle: backLinkTitleFromState,
    counterToGoBack,
    goBackHistory,
  } = useHistoryBackState('/contacts/1');
  const backLinkTitle =
    backLinkTitleFromState ||
    t(TranslationKeys.contact_other, {
      postProcess: PostProcessKeys.TitleCase,
    });

  const {
    data: contactDetails,
    isError: isContactError,
    isLoading: isContactLoading,
    refetch: refetchContact,
  } = useContact({ id: Number(params.id), retry: false });

  const {
    data: contactEmails,
    isError: isContactEmailsError,
    isLoading: isContactEmailsLoading,
  } = useQuery(
    ['Get specific contact by ID', `${params.id}-emails`],
    async () => {
      const response = await restFactory.get<EmailAssociation[]>(
        `/dj/api/v1/contact/${params.id}/emails/`
      );
      return response;
    },
    { cacheTime: 0 }
  );

  const LINKS = [
    {
      to: `${url}/detail`,
      label: 'Details',
      icon: detailsIcon,
    },
  ];

  if (isContactError) {
    return <InvalidDRMLinkMessage />;
  }

  if (isContactLoading || isContactEmailsLoading) {
    return (
      <div className="loadingIndicatorContainer">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!contactDetails || isContactEmailsError || !contactEmails) {
    return <Redirect to="/" />;
  }

  contactDetails.email_associations = contactEmails;

  return (
    <div className={classnames('bg-white h-100')}>
      <div
        className={classnames(
          'd-flex',
          'justify-content-between',
          'bg-light',
          'position-relative',
          'z-index-1',
          'page-title'
        )}
      >
        <div className="container-xl d-flex justify-content-between position-relative fix-bg-images">
          <div
            className={classnames(
              'align-items-center text-truncate align-self-center',
              styles.header
            )}
          >
            <div
              onClick={() => goBackHistory()}
              className={classnames(styles.prevLink, 'text-truncate')}
              data-cy="contact:backlink-to-contacts-list-page"
            >
              <img src={angleIcon} alt="angleIcon"></img>
              <h1
                className={classnames('m-0 text-truncate pr-1')}
                data-testid="contact_title"
              >
                {backLinkTitle}
              </h1>
            </div>
            <div
              className={classnames('text-truncate', styles.title)}
              data-testid="header_name"
              data-cy={`contact:backlink-display-title`}
            >
              {getContactNameToDisplay(contactDetails)}
            </div>
          </div>
          <div
            className={classnames(styles.pills, 'd-flex align-items-center')}
          >
            {LINKS.map((link, index) => {
              return (
                <NavLink
                  key={index}
                  to={{
                    pathname: link.to,
                    state: {
                      counterToGoBack: counterToGoBack - 1,
                      backLinkTitle,
                    },
                  }}
                  className="subNavLink"
                >
                  <Badge
                    pill
                    className="mr-2 mr-lg-5 d-flex align-items-center"
                  >
                    <img src={link.icon} className="mr-2" alt="navIcon"></img>
                    {link.label}
                  </Badge>
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
      <div
        className={classnames(
          'bg-white h-100 px-4',
          styles.routeDetailContainer
        )}
      >
        <Switch>
          <Route path={`${path}/detail`}>
            <ContactDetail contact={contactDetails} refetch={refetchContact} />
          </Route>
        </Switch>
      </div>
    </div>
  );
};

export default connect<ContactsRouterProps>(null)(
  withRouter(ContactsDetailsContainer)
);
